import React, { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Button, Spin, Divider } from "antd";
import ProductCardSkeleton from "./skeletons/ProductCardSkeleton";
import { useNavigate } from "react-router-dom";
import CategoryServ from "../service/CategoryServ";
import ProductServ from "../service/ProductServ";
import ProductCard from "./ProductCard";

import { LocationContext } from "../LocationContext";
import { useDeliveryEstimateContext } from "../context/DeliveryEstimateContext";
import { renderSafeText, findMatchingCategoryIds } from "../util/unitFormatter";

const { Title, Text } = Typography;

const RegularProductListing = ({ filters = {}, hideTitle = false, onDataLoaded, gridSpan, hideShowMore = false, viewType = 'grid' }) => {
    const [products, setProducts] = useState([]);
    const [categoriesCache, setCategoriesCache] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const { latitude, longitude, radius } = React.useContext(LocationContext);
    const deliveryContext = useDeliveryEstimateContext();
    const registerShop = deliveryContext?.registerShop;
    const formatETA = deliveryContext?.formatETA;

    // Register all shops from products only if location is available
    useEffect(() => {
        if (!latitude || !longitude) return;
        products.forEach(product => {
            const shopId = product.shopId?._id || product.shopId;
            if (shopId && registerShop) registerShop(shopId);
        });
    }, [products, registerShop, latitude, longitude]);

    // 1 row of 6 products (xl=4) = 6 products
    const initialLimit = 16;

    useEffect(() => {
        let isCurrent = true;

        const fetchProducts = async () => {
            try {
                setLoading(true);
                // Fetch with a large enough limit
                const params = { limit: 50 };

                if (filters.search) params.search = filters.search; // Use 'search' instead of 'name' for backend consistency

                // Add Location Params
                if (latitude && longitude) {
                    params.lat = latitude;
                    params.lng = longitude;
                    params.radius = radius;
                }

                // Add Category, Brand and Rating Filters
                if (filters.categories && filters.categories.length > 0) {
                    params.categories = filters.categories.join(',');
                }
                if (filters.brands && filters.brands.length > 0) {
                    params.brands = filters.brands.join(',');
                }
                if (filters.rating) {
                    params.rating = filters.rating;
                }

                const textPromise = ProductServ.index(params);

                let catPromise = null;
                if (filters.search && (!filters.categories || filters.categories.length === 0)) {
                    let catList = categoriesCache;
                    if (!catList || catList.length === 0) {
                        try {
                            const catRes = await CategoryServ.index({ limit: "all" });
                            catList = catRes.data || [];
                            setCategoriesCache(catList);
                        } catch (e) {
                            catList = [];
                        }
                    }
                    const matchedCatIds = findMatchingCategoryIds(filters.search, catList);
                    if (matchedCatIds.length > 0) {
                        const catParams = { ...params, categories: matchedCatIds.join(',') };
                        delete catParams.search;
                        catPromise = ProductServ.index(catParams);
                    }
                }

                const [resText, resCat] = await Promise.all([textPromise, catPromise ? catPromise : Promise.resolve(null)]);

                const fetchedText = resText?.data || (Array.isArray(resText) ? resText : []);
                const fetchedCat = resCat?.data || (Array.isArray(resCat) ? resCat : []);

                const combinedRaw = [...fetchedText, ...fetchedCat];
                const seenIds = new Set();
                let fetchedProducts = [];
                combinedRaw.forEach(p => {
                    const pId = p._id || p.id;
                    if (pId && !seenIds.has(pId)) {
                        seenIds.add(pId);
                        fetchedProducts.push(p);
                    }
                });

                // 1. Advanced search sorting first (exact > startsWith > whole word > word start > substring)
                if (filters.search && filters.search.trim()) {
                    const q = filters.search.toLowerCase().trim();
                    const wholeWordRegex = new RegExp(`\\b${q}\\b`, 'i');
                    const wordStartRegex = new RegExp(`\\b${q}`, 'i');

                    fetchedProducts.sort((a, b) => {
                        const aName = (a.name || '').toLowerCase();
                        const bName = (b.name || '').toLowerCase();

                        const aExact = aName === q;
                        const bExact = bName === q;
                        if (aExact && !bExact) return -1;
                        if (!aExact && bExact) return 1;

                        const aStarts = aName.startsWith(q);
                        const bStarts = bName.startsWith(q);
                        if (aStarts && !bStarts) return -1;
                        if (!aStarts && bStarts) return 1;

                        const aWhole = wholeWordRegex.test(aName);
                        const bWhole = wholeWordRegex.test(bName);
                        if (aWhole && !bWhole) return -1;
                        if (!aWhole && bWhole) return 1;

                        const aWordStart = wordStartRegex.test(aName);
                        const bWordStart = wordStartRegex.test(bName);
                        if (aWordStart && !bWordStart) return -1;
                        if (!aWordStart && bWordStart) return 1;

                        return 0;
                    });
                }

                // 2. Apply explicit user sorting (overrides search sorting if it's not the default)
                if (filters.sort) {
                    if (filters.sort === 'price-asc') {
                        fetchedProducts.sort((a, b) => (a.price || 0) - (b.price || 0));
                    } else if (filters.sort === 'price-desc') {
                        fetchedProducts.sort((a, b) => (b.price || 0) - (a.price || 0));
                    }
                }

                if (!isCurrent) return;

                setProducts(fetchedProducts);
                if (onDataLoaded) {
                    onDataLoaded(fetchedProducts.length);
                }
            } catch (err) {
                console.error("Error fetching products:", err);
                if (!isCurrent) return;
                if (onDataLoaded) onDataLoaded(0);
            } finally {
                if (isCurrent) {
                    setLoading(false);
                }
            }
        };

        fetchProducts();

        return () => {
            isCurrent = false;
        };
    }, [JSON.stringify(filters), latitude, longitude, radius]);

    if (loading && products.length === 0) {
        return (
            <div className="regular-product-listing pcu-padding-bottom-40">
                {!hideTitle && (
                    <div className="pcu-margin-bottom-25" style={{ borderBottom: '2px solid var(--color-border-muted)', paddingBottom: '10px' }}>
                        <Title level={3} className="pcu-nearzon-purple pcu-margin-0" style={{ borderLeft: '4px solid var(--nearzon-orange)', paddingLeft: '12px' }}>Explore Our Products</Title>
                    </div>
                )}
                <Row gutter={[12, 24]}>
                    {[...Array(16)].map((_, i) => (
                        <Col xs={12} sm={12} md={6} lg={3} xl={3} key={i}>
                            <ProductCardSkeleton />
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    const visibleProducts = expanded ? products : products.slice(0, initialLimit);

    return (
        <div className="regular-product-listing pcu-padding-bottom-40">
            {!hideTitle && (
                <div className="pcu-margin-bottom-25" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid var(--color-border-muted)', paddingBottom: '10px' }}>
                    <Title level={3} className="pcu-nearzon-purple pcu-margin-0" style={{ borderLeft: '4px solid var(--nearzon-orange)', paddingLeft: '12px', margin: 0 }}>Explore Our Products</Title>
                    {!hideShowMore && products.length > initialLimit && (
                        <Button
                            type="primary"
                            onClick={() => {
                                const queryParams = new URLSearchParams();
                                if (filters.search) queryParams.set("search", filters.search);
                                if (filters.categories?.length > 0) {
                                    filters.categories.forEach(cat => queryParams.append("category", cat));
                                }
                                if (filters.brands?.length > 0) {
                                    filters.brands.forEach(brand => queryParams.append("brand", brand));
                                }
                                navigate(`/all-products?${queryParams.toString()}`);
                            }}
                            className="premium-view-more-btn"
                        >
                            Show More
                        </Button>
                    )}
                </div>
            )}


            <Row gutter={[10, 15]} justify="start">
                {(() => {
                    // Deduplicate products by name for extra safety (merges identical products with different IDs)
                    // Safety: Filter out inactive products
                    const uniqueProducts = Array.from(new Map(
                        products
                            .filter(p => p.isActive !== false)
                            .map(p => [renderSafeText(p.name).toLowerCase(), p])
                    ).values());

                    const finalVisible = expanded ? uniqueProducts : uniqueProducts.slice(0, initialLimit);

                    return finalVisible.map((product) => (
                        <Col key={product._id} {...(viewType === 'list' ? { xs: 24, sm: 24, md: 24, lg: 12, xl: 12 } : (gridSpan || { xs: 12, sm: 12, md: 6, lg: 3, xl: 3 }))}>
                            <ProductCard product={product} formatETA={formatETA} viewType={viewType} />
                        </Col>
                    ));
                })()}
            </Row>
        </div>
    );
};

export default RegularProductListing;
